import LoadingButton from "@mui/lab/LoadingButton";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { API_URL, handleSendMessage } from "../../utilities";
import { useAuth } from "../../context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import TaskForm from "./components/task-form";
import { StatusGlobal } from "src/interface/enum/StatusGlobal";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { TypeUser } from "src/interface/enum/TypeUser";
import { IAppUser } from "src/interface/IUser";
const validationSchema = Yup.object({
  statusTask: Yup.string().required("این فیلد الزامی است"),
  dueDate: Yup.mixed().required("تاریخ سررسید الزامی است"),
  userRef: Yup.string().required("این فیلد الزامی است"),
  customerRef: Yup.string().required("این فیلد الزامی است"),
  titleTask: Yup.string().required("این فیلد الزامی است"),
});

const getInitialValues = () => ({
  priority: undefined,
  dueDate: new Date(),
  createdOn: new Date(),
  desc: "",
  statusTask: StatusGlobal.Reported,
  statusDesc: "",
  userRef: "",
  customerRef: "",
  titleTask: "",
});

const NewTask = ({
  open,
  handleClose,
  refetch,
}: {
  open: boolean;
  handleClose: () => void;
  refetch: () => void;
}) => {
  const { user } = useAuth();
  const { mutateAsync: insertAsync, isPending } = useMutation({
    mutationFn: async (body: any) => {
      if (!user?.token) {
        throw new Error("توکن احراز هویت موجود نیست");
      }

      const response = await fetch(API_URL + "tasks", {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json-patch+json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify(body),
      });
      return await response.json();
    },
    onSuccess: (responseData) => {
      JSON.stringify(responseData);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const notifications = useNotifications();
  const [getEditors, setEditors] = useState<IAutoCompleteLabelValue[]>([]);

  const { mutateAsync: fetchEditors } = useMutation({
    mutationFn: async () => {
      if (!user?.token) {
        throw new Error("توکن احراز هویت موجود نیست");
      }

      const response = await fetch(
        API_URL + "user/getByRole/" + TypeUser.EDITOR,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`خطا در دریافت اطلاعات: ${response.status}`);
      }

      const text = await response.text();
      if (!text) {
        return [];
      }

      try {
        return JSON.parse(text);
      } catch (error) {
        console.error("خطا در پردازش JSON:", text);
        throw new Error("خطا در پردازش پاسخ سرور");
      }
    },
    onSuccess: (data: IAppUser[]) => {
      setEditors(
        data.map((item: IAppUser) => ({
          label: item.fName + " " + item.lName,
          value: item.id,
          item: item,
        }))
      );
    },
    onError: (error) => {
      // Only show error if dialog is open
      if (open) {
        handleSendMessage(
          notifications,
          "خطا در دریافت اطلاعات: " + error,
          "error"
        );
      }
      console.log(error);
    },
  });

  // فراخوانی mutate برای اجرای درخواست
  useEffect(() => {
    if (open && user?.token) {
      fetchEditors();
    }
  }, [open, user?.token]);

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: async (values) => {
      let res;

      res = await insertAsync({
        ...values,
        dueDate: new Date(values.dueDate),
        createdOn: new Date(values.createdOn),
      });
      console.log("🚀 ~ onSubmit: ~ res:", res);

      if (res.status === true) {
        notifications.show("وظیفه جدید با موفقیت ثبت شد!", {
          key: "new-task",
          severity: "success",
          autoHideDuration: 5000,
        });

        refetch();
        handleClose();
      } else {
        notifications.show("خطا در ذخیره سازی!", {
          key: "err-task",
          severity: "error",
          autoHideDuration: 5000,
        });
      }
      formik.resetForm({ values: getInitialValues() });
    },
  });

  // با باز شدن دیالوگ، مقادیر فرم (از جمله تاریخ سررسید = امروز) ریست می‌شود
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      formik.resetForm({ values: getInitialValues() });
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- فقط هنگام باز شدن دیالوگ
  }, [open]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        PaperProps={{
          component: "form",
          onSubmit: formik.handleSubmit,
        }}
      >
        <DialogTitle>وظیفه جدید</DialogTitle>
        <DialogContent>
          <TaskForm formik={formik} />
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={isPending}
            variant="contained"
            color="success"
            size="medium"
            fullWidth
            type="submit"
          >
            ذخیره
          </LoadingButton>
          <Button color="error" variant="contained" onClick={handleClose}>
            انصراف
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default NewTask;
